// Build payloads for Enterprise Helpdesk ticket creation
// Keeps workflow-specific fields under custom_fields

export function buildTicketPayload(type, values) {
  const base = {
    title: values?.title || values?.subject || 'Support Request',
    description: values?.description || values?.details || '',
    priority: values?.priority || 'medium',
    type: 'support',
    category: 'general',
    custom_fields: {}
  };

  switch (type) {
    case 'access_staff_movement':
    case 'staff_movement': {
      const name = values?.user_full_name || values?.name || `${values?.first_name || ''} ${values?.last_name || ''}`.trim();
      const title = `Staff Movement: ${name}`;
      const description = values?.comment || values?.description || '';

      return {
        title,
        description,
        priority: values?.priority || 'medium',
        type: 'staff_movement',
        category: 'access_staff_movement',
        custom_fields: {
          staff_id: values?.staff_id || null,
          employee_number: values?.employee_number || '',
          current_department: values?.current_department || '',
          current_position: values?.current_position || '',
          new_department: values?.department || '', // Map department to new_department
          new_position: values?.assigned_post || '', // Map assigned_post to new_position
          effective_date: values?.required_by || '',
          comment: values?.comment || '',
          declaration: !!values?.declaration,
          // Software requirements - include all fields like new_user
          software_computer: !!values?.software_computer,
          software_edams_billing: !!values?.software_edams_billing,
          software_edams_operations: !!values?.software_edams_operations,
          software_asset_management: !!values?.software_asset_management,
          software_office: !!values?.software_office,
          software_imqs: !!values?.software_imqs,
          software_other: values?.software_other || '',
          // Edams modification fields
          edams_modification: values?.edams_modification || '',
          application_type: values?.edams_modification || ''
        }
      };
    }
    case 'new_user': {
      const name = `${values?.first_name || ''} ${values?.last_name || ''}`.trim();
      const title = `New User Setup: ${name}`;
      const description = values?.comment || values?.description || '';

      return {
        title,
        description,
        priority: values?.priority || 'medium',
        type: 'new_user',
        category: 'new_user',
        custom_fields: {
          title: values?.title || '',
          first_name: values?.first_name || '',
          last_name: values?.last_name || '',
          date_birth: values?.date_birth || '',
          department: values?.department || '',
          division: values?.division || '',
          post: values?.post || '',
          employee: values?.employee || '',
          required_by: values?.required_by || '',
          // Software requirements
          software_computer: !!values?.software_computer,
          software_edams_billing: !!values?.software_edams_billing,
          software_edams_operations: !!values?.software_edams_operations,
          software_asset_management: !!values?.software_asset_management,
          software_office: !!values?.software_office,
          software_imqs: !!values?.software_imqs,
          software_other: values?.software_other || '',
          // Edams fields
          edams_modification: values?.edams_modification || '',
          comment: values?.comment || '',
          declaration: !!values?.declaration
        }
      };
    }
    case 'access_modification': {
      const name = `${values?.first_name || ''} ${values?.last_name || ''}`.trim();
      const title = `Access Modification: ${name}`;
      const description = values?.comment || values?.description || '';

      return {
        title,
        description,
        priority: values?.priority || 'medium',
        type: 'access_modification',
        category: 'access_modification',
        custom_fields: {
          title: values?.title || '',
          first_name: values?.first_name || '',
          last_name: values?.last_name || '',
          department: values?.department || '',
          current_post: values?.current_post || '',
          post: values?.post || '',
          edams_modification: values?.edams_modification || 'modification',
          priority: values?.priority || 'medium',
          limit: values?.limit || 'No change required',
          new_limit: values?.new_limit || '',
          from_date: values?.from_date || '',
          to_date: values?.to_date || '',
          comment: values?.comment || '',
          staff_id: values?.staff_id || '',
          declaration: !!values?.declaration
        }
      };
    }
    case 'access_termination': {
      const name = `${values?.first_name || ''} ${values?.last_name || ''}`.trim();
      const title = `Access Termination: ${name}`;
      const description = values?.comment || values?.description || '';

      return {
        title,
        description,
        priority: values?.priority || 'medium',
        type: 'access_termination',
        category: 'access_termination',
        custom_fields: {
          title: values?.title || '',
          first_name: values?.first_name || '',
          last_name: values?.last_name || '',
          department: values?.department || '',
          current_post: values?.current_post || '',
          edams_modification: values?.edams_modification || 'termination',
          priority: values?.priority || 'medium',
          comment: values?.comment || '',
          staff_id: values?.staff_id || '',
          declaration: !!values?.declaration
        }
      };
    }
    case 'password_reset': {
      const title = `Password Reset: ${values?.supplier_name || 'Supplier'}`;
      const description = values?.description || values?.business_justification || '';

      return {
        title,
        description,
        priority: values?.priority || 'medium',
        type: 'password_reset',
        category: values?.category || 'password_reset',
        system_id: values?.system_id || '',
        custom_fields: {
          supplier_name: values?.supplier_name || '',
          supplier_email: values?.supplier_email || '',
          contact_person: values?.contact_person || '',
          supplier_phone: values?.supplier_phone || '',
          system_id: values?.system_id || '',
          username: values?.username || '',
          reset_reason: values?.reset_reason || '',
          priority: values?.priority || 'medium',
          business_justification: values?.business_justification || '',
          expected_resolution: values?.expected_resolution || '',
          declaration: !!values?.declaration
        }
      };
    }
    case 'change_request': {
      const title = values?.title || values?.subject || 'Enhancement Request';
      const description = values?.description || values?.details || '';

      return {
        title,
        description,
        priority: values?.priority || 'medium',
        type: 'change_request',
        category: values?.category || 'enhancement',
        system_id: values?.system_id || '',
        custom_fields: {
          enhancement_type: values?.enhancement_type || 'improvement',
          business_justification: values?.business_justification || values?.description || '',
          expected_benefit: values?.expected_benefit || '',
          affected_users: values?.affected_users || '',
          priority: values?.priority || 'medium',
          requested_by: values?.requested_by || '',
          department: values?.department || '',
          system_id: values?.system_id || '',
          declaration: !!values?.declaration
        }
      };
    }
    default: {
      return {
        ...base,
        custom_fields: JSON.stringify(base.custom_fields)
      };
    }
  }
}
